import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Shield, Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, InputAdornment, Typography } from '@mui/material';
import * as yup from 'yup';

import { useActivateUser } from '$components/pages/activate_user/activateUser.state';
import PasswordValidityHighlighter from '$components/pages/activate_user/PasswordValidityHighlighter';
import RotraTextField from '$reactHookForm/v2/RotraTextFieldV2';
import useRotraForm from '$reactHookForm/v2/useRotraForm';
import useMyRotraTranslations from '$utils/useMyRotraTranslations';

type ActivationPasswordFormProps = {
  password: string;
};

//at least 8 characters including at least 3 of the following 4 types of characters:
// a lower-case letter, an upper-case letter, a number, a special character (such as !@#$%^&*).
export const PasswordStepValidation = yup.object().shape({
  password: yup
    .string()
    .trim()
    .matches(
      /^.*(?=.{8,})((?=.*[!@#$%^&*]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/
    )
    .required(),
});

export default function PasswordStep() {
  const { state, actions } = useActivateUser();
  const [showPassword, setShowPassword] = useState(false);

  const { tSpecific } = useMyRotraTranslations({
    pathPrefix: 'activate_user',
  });

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const methods = useRotraForm<ActivationPasswordFormProps>({
    formProps: {
      mode: 'all',
      delayError: 350,
      reValidateMode: 'onChange',
      resolver: yupResolver(PasswordStepValidation),
      defaultValues: {
        password: state.password,
      },
    },
    onChange: (value: ActivationPasswordFormProps) => {
      actions.updatePassword(value.password);
    },
  });

  const watchPassword = methods.watch('password');

  useEffect(() => {
    // methods.setValue('password', password?.password ?? '');

    if (methods.getValues().password) {
      methods.trigger('password').catch((error) => {
        throw error;
      });
    }
  }, [methods]);

  return (
    <>
      <Helmet>
        <title>{tSpecific('txt_secure_your_account')}</title>
      </Helmet>

      <FormProvider {...methods}>
        <Typography variant="h5">
          <Shield sx={{ mr: 1, verticalAlign: 'middle' }} />
          {tSpecific('txt_secure_your_account')}
        </Typography>
        <br />
        <Typography variant="body1" sx={{
          mb: 3
        }}>
          {tSpecific(
            'txt_protect_your_account_by_creating_a_strong_and_secure_password'
          )}
        </Typography>
        <RotraTextField
          required
          size="small"
          label={tSpecific('lbl_password')}
          variant="outlined"
          fieldPath="password"
          type={showPassword ? 'text' : 'password'}
          name="myrotra-password"
          showErrorMessage={false}
          slotProps={{
            input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          },
          }}
        />
        <PasswordValidityHighlighter password={watchPassword} />
      </FormProvider>
    </>
  );
}
